import { giphyLightCharcoal, giphyRed } from '@giphy/js-brand'
import { Button, Toggle } from '@giphy/react-giphy-brand'
import React, { useContext } from 'react'
import { useLocation } from 'react-router'
import { deleteChannel, editChannel, fetchChannel, saveChannel } from 'shared/api/channels'
import { IChannel } from 'shared/types/channel'
import styled, { css } from 'styled-components'
import MessageContext from '../context/message'
import RefreshDataContext from '../context/refresh-data'
import { CollectionEditorContext } from './editor'
import { Label } from './sc'
const Container = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    border-top: 1px solid ${giphyLightCharcoal};

    ${Button} {
        width: 33%;
    }
`

const Right = styled.div<{ hide: boolean }>`
    text-align: right;
    min-width: 150px;
    ${Label} {
        color: ${giphyRed};
        display: flex;
        justify-content: flex-end;
        align-items: center;
        span {
            margin-right: 6px;
        }
    }
    ${props =>
        props.hide &&
        css`
            visibility: hidden;
            pointer-events: none;
        `}
    ${Label} {
        cursor: pointer;
    }
`
const Left = styled.div`
    display: flex;
    min-width: 150px;
    align-items: center;
    ${Label} {
        margin-right: 5px;
    }
`
const ToggleText = styled.div`
    font-size: 10px;
`
const Footer = () => {
    const { updateChannel, channel, newCollectionProps, onComplete, setDisabled } = useContext(CollectionEditorContext)
    const { pathname } = useLocation()
    const { refreshChannel } = useContext(RefreshDataContext)
    const { sendMessage } = useContext(MessageContext)

    const togglePrivacy = (isOn: boolean) => {
        updateChannel({ is_private: isOn })
    }
    const save = async () => {
        setDisabled(true)
        if (newCollectionProps) {
            const { parentChannel: parent, userId, redirectOnCreate } = newCollectionProps
            try {
                const result = await saveChannel({
                    display_name: channel.display_name!,
                    description: channel.description!,
                    slug: '',
                    user: userId,
                    parent: parent.id,
                    is_private: channel.is_private,
                })
                // the parent channel will refresh fetching the children
                refreshChannel({ ...parent, has_children: true })
                sendMessage(`${channel.display_name} collection created`, 'success')
                if (redirectOnCreate) {
                    setTimeout(() => (location.href = result.url), 1500)
                } else {
                    // this closes the panel and we don't want that on redirect.
                    // but maybe we'd want to respond in some other way?
                    onComplete?.()
                }
            } catch (error) {
                console.error(`Error creating collection`, error)
                sendMessage(`Error creating collection`, 'error')
            }
        } else if (channel) {
            try {
                const result = await editChannel(channel as IChannel)
                sendMessage(`Collection saved`, 'success')
                // new thing, refreshing instead of reloading
                // kind of required for the modal workflow design
                refreshChannel({ ...channel, ...result, user: channel.user })
                onComplete?.()
                // location.reload()
            } catch (error) {
                console.error(`Collection edit error`, error)
                sendMessage(`Error editing collection`, 'error')
            }
        }
        if (!newCollectionProps?.redirectOnCreate) {
            setDisabled(false)
        }
    }
    const deleteCollection = async () => {
        if (window.confirm(`Are you sure you want to delete this collection?`)) {
            setDisabled(true)
            try {
                await deleteChannel(channel.id!)
                const parentChannel = await fetchChannel(channel.parent!)
                // remove children from the deleted channel
                refreshChannel({ ...(channel as IChannel), has_children: false })
                refreshChannel(parentChannel)
                sendMessage(`Collection deleted!`, 'success')
                if (pathname === channel.url) {
                    // if we're on the channel we just deleted, redirect
                    setTimeout(() => (location.href = parentChannel.url), 1500)
                } else {
                    onComplete?.()
                }
            } catch (error) {
                console.error(`Collection delete error`, error)
                sendMessage(`Error deleting collection`, 'error')
                setDisabled(false)
            }
        }
    }
    const isNewChannel = !!newCollectionProps
    const isRootChannel = !channel.parent // parent 0 means we're on the user's root
    return (
        <Container>
            <Left>
                <Label>Public</Label>
                <Toggle
                    isOn={!channel.is_private}
                    width={60}
                    toggle={togglePrivacy}
                    onElement={<ToggleText>ON</ToggleText>}
                    offElement={<ToggleText>OFF</ToggleText>}
                />
            </Left>
            <Button disabled={!channel.display_name} onClick={save}>
                {isNewChannel ? 'Create' : 'Save'} Collection
            </Button>
            <Right hide={isNewChannel || isRootChannel}>
                <Label onClick={deleteCollection}>
                    <span className="ss-trash" /> Delete
                </Label>
            </Right>
        </Container>
    )
}

export default Footer
